import { lazy, Suspense, useState, useRef, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Analytics } from "@vercel/analytics/react";
import { Toaster, toast } from "sonner";
import L from "leaflet";
import { Compass, Route as RouteIcon, List, Plus, Minus } from "./data/design-tokens";
import { TripProvider, useTrip } from "./contexts/TripContext";
import { SearchProvider, useSearch } from "./contexts/SearchContext";
import { AuthProvider } from "./contexts/AuthContext";
import { MapView } from "./components/MapView";
import { SearchPanel } from "./components/SearchPanel";
import { useAuth, LoginModal, AuthButton } from "./components/LoginModal";
import { useTrainAnimation } from "./hooks/useTrainAnimation";
import { initCityStore } from "./services/cityStore";
import { reportError } from "./services/errorService";
import type { City } from "./data/types";

// Lazy-load panels that aren't visible on initial render
const StopDiscoveryCard = lazy(() =>
  import("./components/StopDiscoveryCard").then((m) => ({ default: m.StopDiscoveryCard }))
);
const ExplorePanel = lazy(() =>
  import("./components/ExplorePanel").then((m) => ({ default: m.ExplorePanel }))
);
const TripPanel = lazy(() =>
  import("./components/TripPanel").then((m) => ({ default: m.TripPanel }))
);

export default function App() {
  // One-time app boot: load city data from Supabase into memory
  useEffect(() => {
    const initialize = () => {
      void initCityStore().catch((err) => {
        reportError(err, { level: "critical", context: { op: "initCityStore" } });
        toast.error("Failed to load station data — some features may not work.", {
          id: "city-startup", action: { label: "Retry", onClick: initialize },
        });
      });
    };
    initialize();
    window.addEventListener("online", initialize);
    return () => window.removeEventListener("online", initialize);
  }, []);

  return (
    <AuthProvider>
      <TripProvider>
        <SearchProvider>
          <AppShell />
          <Toaster position="top-right" richColors duration={5000} />
          <Analytics />
        </SearchProvider>
      </TripProvider>
    </AuthProvider>
  );
}

// ─── Inner Shell (has access to both contexts) ──────────────────────────

function AppShell() {
  const trip = useTrip();
  const search = useSearch();
  const { user } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [searchPanelVisible, setSearchPanelVisible] = useState(true);

  // Train animation — fully self-contained via its own Leaflet layer group
  const mapRef = useRef<L.Map | null>(null);
  const { trainEnabled, setTrainEnabled } = useTrainAnimation(mapRef);
  const handleMapRef = useCallback((map: L.Map) => { mapRef.current = map; }, []);

  const handleSearchConnection = (from: City, to: City) => {
    setSearchPanelVisible(true);
    search.handleSearch(from, to, trip.getConnectionDeparture(from));
  };

  // Composed toggle handlers that bridge selected stop + search origin
  const handleToggleFood = () => {
    if (trip.selectedStop) {
      const split = trip.handleToggleFood(trip.selectedStop, search.fromStation);
      if (split?.dest) {
        setSearchPanelVisible(true);
        search.handleSearch(trip.selectedStop, split.dest, { customDate: split.customDate, customTime: split.customTime });
      }
    }
  };
  const handleToggleOvernight = () => {
    if (trip.selectedStop) {
      const split = trip.handleToggleOvernight(trip.selectedStop, search.fromStation);
      if (split?.dest) {
        setSearchPanelVisible(true);
        search.handleSearch(trip.selectedStop, split.dest, { customDate: split.customDate, customTime: split.customTime });
      }
    }
  };
  const handleToggleFika = () => {
    if (trip.selectedStop) {
      const split = trip.handleToggleFika(trip.selectedStop, search.fromStation);
      if (split?.dest) {
        setSearchPanelVisible(true);
        search.handleSearch(trip.selectedStop, split.dest, { customDate: split.customDate, customTime: split.customTime });
      }
    }
  };

  return (
    <div className="h-screen w-screen relative overflow-hidden">
      <MapView
        routes={search.routes}
        activeRouteIndex={search.activeRouteIndex}
        selectedStop={trip.selectedStop}
        stopActions={trip.stopActions}
        viewMode={search.viewMode}
        tripStops={trip.tripStops}
        tripLegs={trip.mapTripLegs}
        tripStationDayMap={trip.tripStationDayMap}
        routeStartDay={search.routeStartDay}
        allPOIs={trip.allPOIs}
        routePOIs={search.routePOIs}
        tripPOIs={trip.tripPOIs}
        exploreFilters={search.exploreFilters}
        searchPanelVisible={searchPanelVisible && search.viewMode === "search"}
        tripPanelVisible={trip.tripPanelVisible}
        onSelectRoute={search.handleSelectRoute}
        onSelectStop={trip.setSelectedStop}
        onSelectStopWithPosition={trip.selectStopWithPosition}
        onSelectedStopPositionUpdate={trip.updateCardPosition}
        onDeselectStop={trip.deselectStop}
        poiConnections={trip.poiConnections}
        onAddRouteToTrip={search.handleAddRouteToTripFromMap}
        onSearchConnection={handleSearchConnection}
        onMapReady={search.setMapControls}
        onMapRef={handleMapRef}
        onSetHoveredPOI={trip.setHoveredPOI}
        onClearHoveredPOI={trip.clearHoveredPOI}
      />

      {search.viewMode === "search" && (search.routes.length > 0 || trip.tripLegs.length > 0) && (
        <div className="absolute bottom-10 left-1/2 z-[999] -translate-x-1/2 rounded-lg border border-gray-200 bg-white/95 px-3 py-2 shadow-sm">
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-gray-700" aria-label="Map line legend">
            {trip.mapTripLegs.some(leg => !leg.requiresSelection) && <span className="flex items-center gap-2"><span className="w-7 border-t-[3px] border-violet-500" />Your trip · day colors</span>}
            {search.routes.length > 0 && <span className="flex items-center gap-2"><span className="w-7 border-t-[3px] border-dashed border-indigo-600" />Preview · choose an option</span>}
            {trip.pendingConnection && <span className="flex items-center gap-2"><span className="w-7 border-t-[3px] border-dotted border-slate-400" />Choose a train</span>}
          </div>
        </div>
      )}

      {/* Custom Zoom Controls */}
      {search.mapControls && (
        <div className="absolute bottom-6 right-4 z-[1001] flex flex-col gap-0 rounded-lg overflow-hidden shadow-lg">
          <button
            onClick={search.mapControls.zoomIn}
            className="w-8 h-8 flex items-center justify-center border transition-colors bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
            title="Zoom in"
          >
            <Plus className="size-4" />
          </button>
          <button
            onClick={search.mapControls.zoomOut}
            className="w-8 h-8 flex items-center justify-center border border-t-0 transition-colors bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
            title="Zoom out"
          >
            <Minus className="size-4" />
          </button>
        </div>
      )}

      {/* View Mode Switcher + Trip Toggle */}
      <div className="absolute top-4 right-4 z-[1000] flex items-center gap-2">
        <div className="flex gap-2">
          <button
            onClick={() => { search.setViewMode("search"); setSearchPanelVisible((v) => search.viewMode === "search" ? !v : true); }}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg border transition-all ${search.viewMode === "search" && searchPanelVisible
              ? "bg-indigo-600 text-white border-indigo-600"
              : "bg-white/95 text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            title="Search Routes"
          >
            <List className="size-4" />
            <span className="text-[12px]" style={{ fontWeight: 500 }}>Search</span>
          </button>
          <button
            onClick={() => search.setViewMode("explore")}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg border transition-all ${search.viewMode === "explore"
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-white/95 text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            title="Explore: Scenic Routes, Heritage Landmarks & Hidden Gems"
          >
            <Compass className="size-4" />
            <span className="text-[12px]" style={{ fontWeight: 500 }}>Explore</span>
          </button>
        </div>

        <button
          onClick={trip.handleToggleTripPanel}
          className={`relative flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg border transition-all ${trip.tripPanelVisible
            ? "bg-indigo-600 text-white border-indigo-600"
            : "bg-white/95 text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
          title="Trip Planner"
        >
          <RouteIcon className="size-4" />
          <span className="text-[12px]" style={{ fontWeight: 500 }}>Trip</span>
          {trip.tripStops.length > 0 && !trip.tripPanelVisible && (
            <span
              className="absolute -top-1.5 -right-1.5 bg-indigo-500 text-white text-[9px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-white shadow-sm"
              style={{ fontWeight: 700 }}
            >
              {trip.tripStops.length}
            </span>
          )}
        </button>

        {/* Auth Button */}
        <AuthButton
          user={user}
          onLoginClick={() => setLoginOpen(true)}
          trainEnabled={trainEnabled}
          onTrainToggle={setTrainEnabled}
        />
      </div>

      {/* Panels based on view mode */}
      {search.viewMode === "search" && searchPanelVisible && (
        <SearchPanel onClose={() => setSearchPanelVisible(false)} />
      )}

      {search.viewMode === "explore" && (
        <Suspense fallback={null}>
          <ExplorePanel
            onClose={() => search.setViewMode("search")}
            filters={search.exploreFilters}
            onFiltersChange={search.setExploreFilters}
          />
        </Suspense>
      )}

      {trip.tripPanelVisible && (
        <Suspense fallback={null}>
          <TripPanel
            stops={trip.tripStops}
            tripName={trip.tripName}
            onTripNameChange={trip.setTripName}
            onRemoveStop={trip.handleRemoveFromTrip}
            onReorderStop={trip.handleReorderStop}
            onSetNights={trip.handleSetNights}
            onClearTrip={trip.handleClearTrip}
            onClose={trip.handleCloseTripPanel}
            onStopClick={trip.handleTripStopClick}
            onExport={trip.handleExportTrip}
            travelDate={trip.travelDate}
            stopActions={trip.stopActions}
            tripLegs={trip.tripLegs}
            tripDaySegments={trip.tripDaySegments}
            discoveryValue={trip.discoveryValue}
            onSearchGap={handleSearchConnection}
            isLoggedIn={!!user}
            activeTripId={trip.activeTripId}
            savedTrips={trip.savedTrips}
            savedTripsLoading={trip.savedTripsLoading}
            onSaveTrip={trip.saveCurrentTrip}
            onSaveAsNewTrip={trip.saveAsNewTrip}
            onLoadTrip={trip.loadSavedTrip}
            onDeleteTrip={trip.deleteSavedTrip}
            onRefreshSavedTrips={trip.refreshSavedTrips}
            onRenameTrip={trip.renameSavedTrip}
            shareToken={trip.activeShareToken}
            onSaveForShare={user ? trip.saveCurrentTrip : undefined}
            staleLegKeys={trip.staleLegKeys}
          />
        </Suspense>
      )}

      {/* POI hover overlay — compact StopDiscoveryCard */}
      <AnimatePresence>
        {trip.hoveredPOI && trip.hoveredPOIPosition && !trip.selectedStop && (
          <motion.div
            key={`hover-${trip.hoveredPOI.id}`}
            initial={{ opacity: 0, scale: 0.92, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 8 }}
            transition={{ type: "spring", stiffness: 400, damping: 28, mass: 0.8 }}
            style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1002 }}
          >
            <Suspense fallback={null}>
              <StopDiscoveryCard
                station={trip.hoveredPOI}
                cardPosition={trip.hoveredPOIPosition}
                size="compact"
                onToggleFood={() => {}}
                onToggleOvernight={() => {}}
                onClose={() => {}}
              />
            </Suspense>
          </motion.div>
        )}
      </AnimatePresence>

      {/* StopDiscoveryCard with enter/exit animation */}
      <AnimatePresence>
        {trip.selectedStop?.id && trip.selectedStop.latitude !== undefined && trip.selectedStop.longitude !== undefined && trip.cardPosition && (
          <motion.div
            key={`card-${trip.selectedStop.id}`}
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.88 }}
            transition={{
              type: "spring",
              stiffness: 350,
              damping: 26,
              mass: 0.8,
            }}
            style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1003 }}
          >
            <Suspense fallback={null}>
              <StopDiscoveryCard
                station={trip.selectedStop}
                cardPosition={trip.cardPosition}
                actions={trip.stopActions[trip.selectedStop.id]}
                hasOnwardConnection={trip.selectedStopHasOnwardConnection}
                travelDate={trip.selectedStopDate}
                nights={trip.stopActions[trip.selectedStop.id]?.nights || 1}
                onToggleFood={handleToggleFood}
                onToggleOvernight={handleToggleOvernight}
                onToggleFika={handleToggleFika}
                onClose={trip.deselectStop}
              />
            </Suspense>
          </motion.div>
        )}
      </AnimatePresence>

      {!search.searchPerformed && !search.loading && search.routes.length === 0 && search.viewMode === "search" && !trip.tripPanelVisible && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[999]">
          <div className="bg-white/90 backdrop-blur-sm rounded-full px-5 py-2.5 shadow-lg border border-gray-200/80 text-[13px] text-gray-500 flex items-center gap-2">
            <span className="text-indigo-500">&#9679;</span>{" "}
            Select departure and destination to find train routes across Europe
          </div>
        </div>
      )}

      {search.searchPerformed && !search.loading && search.routes.length === 0 && search.viewMode === "search" && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[999]">
          <div className="bg-white/90 backdrop-blur-sm rounded-full px-5 py-2.5 shadow-lg border border-gray-200/80 text-[13px] text-gray-500">
            No routes found between these cities. Try a different combination.
          </div>
        </div>
      )}

      {/* Legal Footer */}
      <div className="absolute bottom-2 left-2 z-[999]">
        <div className="flex items-center gap-2 text-[10px] text-gray-400">
          <a
            href="/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-600 transition-colors"
          >
            Terms
          </a>
          <span>·</span>
          <a
            href="/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-600 transition-colors"
          >
            Privacy
          </a>
          <span>·</span>
          <a
            href="/privacy.html#attributions"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-600 transition-colors"
          >
            Attributions
          </a>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal open={loginOpen} onOpenChange={setLoginOpen} />
    </div>
  );
}
